import {Alert, Box, Typography, Zoom, Chip, useTheme, Stack, Menu, MenuItem} from '@mui/material';
import {LoadingConfigurationsType, ThemeMode} from '../../../../../../enums';
import {CarePlanActivityTypesArrangement} from '../../../../../../backend/enums/care-plan-activity-types-arrangement';
import {useAppContext} from '../../../../../../contexts/app/context';
import {CardActions} from '../../../../../../components/care-plan/activities-group-entry/card-actions';
import {useExportDataWithErrorHandler} from '../../../../../../hooks/use-export-data-with-error-handler';
import {translations} from '../../../../../../i18n/translations';
import {Trans, useTranslation} from 'react-i18next';
import {Tooltip} from '../../../../../../components/tooltip/tooltip';
import {ReactSVG} from 'react-svg';
import {
  AutonomyAssessmentConfigurationInfo,
  ConfigurationValueType,
  ConfigurationsIndex,
  ConfigurationsValuesIndex,
} from '../../../../../../types';
import {Pallete} from '../../../../../../theme/bluebird';
import {CarePlanSheetActivitiesGroupEntryConfigurationValue} from '../../../../../../components/care-plan/activities-group-entry-configuration-value/activities-group-entry-configuration-value';
import {useEffect, useMemo, useState} from 'react';
import {EntityActivitiesRepository} from '../../../../../../repositories/entity-activities';
import {useAuth} from '../../../../../../contexts/auth/context';
import {
  MEALS_GROUP_ID,
  MEALS_SECTOR_ID,
  SECTOR_ID_TO_PERMISSION_TYPE_MAPPING,
} from '../../../../../../utils/constants';
import {convertArrayToIndex} from '../../../../../../utils/functions';
import {CarePlanConfiguration} from '../../../../../../backend/interfaces/care-plan-configuration';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../../../../backend/requests/care-plan-activities-group';
import {CarePlanConfigurationFeature} from '../../../../../../backend/enums/care-plan-configuration-feature';
import {ActivitiesGroup} from '../../../../../../backend/interfaces/activities-group';
import {CarePlanConfigurationItem} from '../../../../../../backend/interfaces/care-plan-configuration-item';
import {NoActivities} from '../../../../../../components/care-plan/activities-group-entry/no-activities';
import {CarePlanActivitiesGroupType} from '../../../../../../backend/interfaces/care-plan-activities-group-type';
import {ArrowDropDown} from '@mui/icons-material';
import {CarePlanSheetActivityTypeEntry} from '../../../../../../components/care-plan/activity-type-entry/activity-type-entry';
import {useFetchAllItems} from '../../../../../../hooks/fetch-all-items';
import {useParams} from 'react-router-dom';
import {ActivityType} from '../../../../../../backend/interfaces/activity-type';
import {ActivitySkeleton} from '../../../../../../components/care-plan/activities-group-entry/skeleton';
import {exportCarePlanGetReportData} from '../../../../../../utils/data/export-data/care-plan/base/export-care-plan-get-report-data';
import {prepareExportCarePlanPDF} from '../../../../../../utils/data/export-data/care-plan/base/export-care-plan-pdf';
import {prepareExportCarePlanXLSX} from '../../../../../../utils/data/export-data/care-plan/base/export-care-plan-xlsx';
import {useSeniorSidebar} from '../../../../../../contexts/seniors-sidebar/context';
import {exportCarePlanIsReady} from '../../../../../../utils/data/export-data/care-plan/base/export-care-plan-is-ready';

type ActivitiesGroupCardProps = {
  id?: string;
  activitiesSectorId: string;
  activitiesGroupId: string;
  activitiesGroupConfigurations?: Array<{
    carePlanConfigurationId: string;
    carePlanConfigurationValue?: any;
  }>;
  editMode: boolean;
  activityTypes?: Array<CarePlanActivitiesGroupType>;
  setEditMode: (value?: string) => void;
  onOpenCreateActivityGroupEntry: (activityType: ActivityType) => void;
  onOpenEditActivityGroupEntry: (activityType: CarePlanActivitiesGroupType) => void;
  onOpenViewActivityGroupEntry: (activityType: CarePlanActivitiesGroupType) => void;
  onDeleteActivityGroupEntry: (activitiesGroupName: string) => void;
  onEditActivityGroup: (defaultValues: ConfigurationsValuesIndex) => void;
};

export const ActivitiesGroupCard = ({
  id,
  activitiesSectorId,
  activitiesGroupId,
  activitiesGroupConfigurations,
  editMode,
  activityTypes,
  setEditMode,
  onOpenCreateActivityGroupEntry,
  onOpenEditActivityGroupEntry,
  onOpenViewActivityGroupEntry,
  onDeleteActivityGroupEntry,
  onEditActivityGroup,
}: ActivitiesGroupCardProps) => {
  const {t} = useTranslation();
  const {state: authState, hasWritePermission} = useAuth();
  const theme = useTheme();
  const appContext = useAppContext();
  const {previewPdfWithErrorHandling, previewXlsxWithErrorHandling} =
    useExportDataWithErrorHandler();
  let {
    entityId,
    templateId,
    // id: seniorId,
  }: {entityId: string; templateId: string; id: string} = useParams();
  const seniorSidebar = useSeniorSidebar();
  const [taskEntriesLayout, setTaskEntriesLayout] = useState(
    CarePlanActivityTypesArrangement.Columns
  );
  const [configurationsIndex, setConfigurationsIndex] = useState<ConfigurationsIndex>();
  const [notesConfigurationInfo, setNotesConfigurationInfo] =
    useState<ConfigurationValueType<any>>(undefined);
  const [configurationsValuesIndex, setConfigurationsValuesIndex] =
    useState<ConfigurationsValuesIndex>(undefined);
  const [activitiesGroup, setActivitiesGroup] = useState<ActivitiesGroup | null>();
  const [canWrite] = useState(
    hasWritePermission(SECTOR_ID_TO_PERMISSION_TYPE_MAPPING[activitiesSectorId])
  );
  const [loadingConfigurations, setLoadingConfigurations] = useState<LoadingConfigurationsType>(
    LoadingConfigurationsType.Loading
  );
  const [autonomyAssessmentConfiguration, setAutonomyAssessmentConfiguration] =
    useState<AutonomyAssessmentConfigurationInfo>();
  const [activitiesTypesListMenuAnchor, setActivitiesTypesListMenuAnchor] =
    useState<null | HTMLElement>(null);
  // Load periods of the day for the medication group card (needed to map activity type ids in the posology schedule)
  const {items: periodsOfTheDay} = useFetchAllItems({
    fetchFunction: ({limit, skip}) =>
      EntityActivitiesRepository.instance.getTypes(
        skip,
        limit,
        entityId,
        MEALS_SECTOR_ID,
        MEALS_GROUP_ID
      ),
    enabled: taskEntriesLayout === CarePlanActivityTypesArrangement.Medication,
  });
  const [activitiesTypesWithinGroup, setActivitiesTypesWithinGroup] =
    useState<Array<ActivityType>>();

  useEffect(() => {
    EntityActivitiesRepository.instance
      .getAllTypes(entityId, activitiesSectorId, activitiesGroupId)
      .then((data) => {
        setActivitiesTypesWithinGroup(data.filter((item) => item.isActive));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const hasSubActivities = useMemo(
    () => activitiesTypesWithinGroup && activitiesTypesWithinGroup?.length > 0,
    [activitiesTypesWithinGroup]
  );

  // FIX-ME: Temporary - this is here to enable us to search the `CarePlanActivitiesGroup` configurations for the defined group repetition config.
  // That should probably be comming directly on the fields configs per record, inside the dynamic form `configurationsIndex`
  const groupRepetitionConfigurationType = useMemo(() => {
    let type: 'activities-group' | 'activity-type' | undefined = 'activity-type';

    if (activitiesGroupConfigurations) {
      for (const item of activitiesGroupConfigurations) {
        try {
          const jsonValue = JSON.parse(item.carePlanConfigurationValue);
          if (typeof jsonValue === 'object' && jsonValue.hasOwnProperty('type'))
            type = jsonValue.type;
        } catch (error) {
          // Ignore non-parseable JSON values
        }
      }
    }

    return type;
  }, [activitiesGroupConfigurations]);

  useEffect(() => {
    Promise.all([
      // Load Activities Group (to obtain the Configuration layout)
      EntityActivitiesRepository.instance.getGroup(
        authState.session?.selectedAffiliation?.entity.id ?? '',
        activitiesSectorId,
        activitiesGroupId
      ),

      // Load Activities Group Configurations (to list the configuration values)
      EntityActivitiesRepository.instance.getGroupCarePlanConfigurations(
        authState.session?.selectedAffiliation?.entity.id ?? '',
        activitiesSectorId,
        activitiesGroupId
      ),
    ])
      .then(([activitiesGroup, configurations]) => {
        if (activitiesGroup) {
          // Build Configurations Index
          const configurationsIndex = convertArrayToIndex<CarePlanConfiguration>(
            configurations,
            'id'
          );

          // TODO: remove me later, should be sent by the BE!!!
          if (activitiesGroup.name === 'Medicação') {
            activitiesGroup.carePlanActivityTypesArrangement =
              CarePlanActivityTypesArrangement.Medication;
          }

          // Set card layout
          if (activitiesGroup.carePlanActivityTypesArrangement)
            setTaskEntriesLayout(activitiesGroup.carePlanActivityTypesArrangement);

          // Build values index
          const valuesIndex =
            convertArrayToIndex<CarePlanActivitiesGroupApiRequestConfigurationsItem>(
              activitiesGroupConfigurations ?? [],
              'carePlanConfigurationId'
            );

          // Get Id of "Autonomy Assessment" Feature
          let autonomyAssessmentFeatureId: string | undefined = undefined;
          for (const configuration of configurations) {
            if (configuration.feature === CarePlanConfigurationFeature.AutonomyAssessment) {
              // Set id
              autonomyAssessmentFeatureId = configuration.id;

              // Stop search
              break;
            }
          }

          // Get Id of "Notes" Feature
          let notesFeatureId: string | undefined = undefined;
          for (const configuration of configurations) {
            if (configuration.feature === CarePlanConfigurationFeature.Notes) {
              // Set id
              notesFeatureId = configuration.id;

              // Stop search
              break;
            }
          }

          // Evaluate Autonomy Assessment
          if (autonomyAssessmentFeatureId) {
            // If Care Plan Configuration Layout is defined
            if (
              activitiesGroup.carePlanConfigurationLayout &&
              activitiesGroup.carePlanConfigurationLayout.configurations
            ) {
              // Remove it from the Layout
              for (
                let i = 0;
                i < activitiesGroup.carePlanConfigurationLayout.configurations.length;
                i++
              ) {
                if (
                  activitiesGroup.carePlanConfigurationLayout.configurations[i]
                    .carePlanConfigurationId === autonomyAssessmentFeatureId
                ) {
                  // Remove
                  activitiesGroup.carePlanConfigurationLayout.configurations.splice(i, 1);

                  // Stop search
                  break;
                }
              }
            }

            // If entry has configurations
            if (activitiesGroupConfigurations) {
              // Get Autonomy Assessment value
              for (const configuration of activitiesGroupConfigurations) {
                if (configuration.carePlanConfigurationId === autonomyAssessmentFeatureId) {
                  // Set value
                  setAutonomyAssessmentConfiguration({
                    configuration: configurationsIndex[autonomyAssessmentFeatureId],
                    valuesIndex: convertArrayToIndex<CarePlanConfigurationItem>(
                      configurationsIndex[autonomyAssessmentFeatureId].arrayItems ?? [],
                      'id'
                    ),
                    value: configuration.carePlanConfigurationValue,
                  });

                  // Stop search
                  break;
                }
              }
            }
          }

          // Evaluate Notes
          if (notesFeatureId) {
            // If Care Plan Configuration Layout is defined
            if (
              activitiesGroup.carePlanConfigurationLayout &&
              activitiesGroup.carePlanConfigurationLayout.configurations
            ) {
              // Remove it from the Layout
              for (
                let i = 0;
                i < activitiesGroup.carePlanConfigurationLayout.configurations.length;
                i++
              ) {
                if (
                  activitiesGroup.carePlanConfigurationLayout.configurations[i]
                    .carePlanConfigurationId === notesFeatureId
                ) {
                  // Remove
                  activitiesGroup.carePlanConfigurationLayout.configurations.splice(i, 1);

                  // Stop search
                  break;
                }
              }
            }

            // If entry has configurations
            if (activitiesGroupConfigurations) {
              // Get Notes value
              for (const configuration of activitiesGroupConfigurations) {
                if (configuration.carePlanConfigurationId === notesFeatureId) {
                  // Set value
                  setNotesConfigurationInfo({
                    configuration: configurationsIndex[notesFeatureId],
                    value: configuration.carePlanConfigurationValue,
                  });

                  // Stop search
                  break;
                }
              }
            }
          }

          // Set
          setActivitiesGroup(activitiesGroup);

          setConfigurationsIndex(configurationsIndex);

          setConfigurationsValuesIndex(valuesIndex);

          setLoadingConfigurations(LoadingConfigurationsType.Loaded);
        }
      })
      .catch((error) => {
        console.log(error);
        setLoadingConfigurations(LoadingConfigurationsType.Error);
      });
  }, [
    activitiesGroupConfigurations,
    activitiesGroupId,
    activitiesSectorId,
    authState.session?.selectedAffiliation?.entity.id,
  ]);

  const isExportReady = useMemo(
    () => exportCarePlanIsReady(activitiesGroup, appContext.state.configuration.environment),
    [activitiesGroup, appContext.state.configuration.environment]
  );

  if (loadingConfigurations === LoadingConfigurationsType.Loading) return <ActivitySkeleton />;

  return (
    <Box
      className={`activities-group-entry-card
  activities-group-entry-card-${taskEntriesLayout}
  activities-group-entry-card-${editMode ? 'editing' : 'not-editing'}
  anim-border
  `}
      sx={{
        backgroundColor:
          appContext.state.currentThemeMode === ThemeMode.Light
            ? theme.palette.background.paper
            : theme.palette.grey[900],
      }}
    >
      {/* CARD ACTIONS */}
      {canWrite && isExportReady ? (
        <CardActions
          id={id}
          editMode={editMode}
          setEditMode={setEditMode}
          setConfirmDeletion={() => onDeleteActivityGroupEntry(activitiesGroup?.name ?? '')}
          onSaveAsPdf={async () => {
            await previewPdfWithErrorHandling(async () => {
              const senior = seniorSidebar.state?.senior?.originalData;
              const entity = authState.session?.selectedAffiliation?.entity;
              const reportData = await exportCarePlanGetReportData({
                entityId,
                senior,
                activitiesSectorId,
                activitiesGroupId,
              });

              if (!reportData) {
                return null;
              }

              if (!senior) {
                throw new Error('User not found in session');
              }

              if (!entity) {
                throw new Error('Entity not found in session');
              }

              const pdf = await prepareExportCarePlanPDF({
                reportData,
                senior,
                entity,
                t,
              });

              return {
                file: pdf.file,
                fileName: pdf.fileName,
              };
            });
          }}
          onSaveAsXlsx={async () => {
            await previewXlsxWithErrorHandling(async () => {
              const senior = seniorSidebar.state?.senior?.originalData;

              if (!senior) {
                throw new Error('User not found in session');
              }

              const reportData = await exportCarePlanGetReportData({
                entityId,
                senior,
                activitiesSectorId,
                activitiesGroupId,
              });
              if (!reportData) {
                return null;
              }

              const xlsx = await prepareExportCarePlanXLSX({reportData, senior, t});

              return {
                file: xlsx.file,
                fileName: xlsx.fileName,
              };
            });
          }}
        />
      ) : (
        <CardActions
          id={id}
          editMode={editMode}
          setEditMode={setEditMode}
          setConfirmDeletion={() => onDeleteActivityGroupEntry(activitiesGroup?.name ?? '')}
        />
      )}

      {/* LAYOUT */}
      {activitiesGroup && (
        <div className={`card-body card-body-${taskEntriesLayout}`}>
          <div className="col-card col-configurations">
            {loadingConfigurations === LoadingConfigurationsType.Error && (
              <Alert severity="error">{t(translations.activities_group_entry.error)}</Alert>
            )}

            {loadingConfigurations === LoadingConfigurationsType.Loaded && (
              <>
                <div className="col-title">
                  <div className="header">
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <div className="icon">
                        <ReactSVG src={activitiesGroup.iconUrl} title={activitiesGroup.name} />
                      </div>
                      <Typography variant="h5">{activitiesGroup.name}</Typography>
                    </div>

                    {autonomyAssessmentConfiguration &&
                      autonomyAssessmentConfiguration.configuration.arrayItems &&
                      autonomyAssessmentConfiguration.valuesIndex[
                        autonomyAssessmentConfiguration.value
                      ] && (
                        <Tooltip
                          title={
                            autonomyAssessmentConfiguration.valuesIndex[
                              autonomyAssessmentConfiguration.value
                            ].description
                          }
                        >
                          <Chip
                            className="autonomy-assessment"
                            label={
                              autonomyAssessmentConfiguration.valuesIndex[
                                autonomyAssessmentConfiguration.value
                              ].name
                            }
                            sx={{
                              backgroundColor:
                                autonomyAssessmentConfiguration.valuesIndex[
                                  autonomyAssessmentConfiguration.value
                                ].backgroundColor,
                              '.MuiChip-label': {
                                color:
                                  autonomyAssessmentConfiguration.valuesIndex[
                                    autonomyAssessmentConfiguration.value
                                  ].color,
                                flexGrow: 0,
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                  </div>
                </div>

                <div className="col-edit">
                  <Zoom in={editMode} mountOnEnter unmountOnExit>
                    <Typography
                      variant="h5"
                      sx={{color: Pallete.blue.base}}
                      onClick={() => onEditActivityGroup(configurationsValuesIndex)}
                    >
                      {t(translations.activities_group_entry.edit_details)}
                    </Typography>
                  </Zoom>
                </div>

                <div className="col-content">
                  <div
                    className={`activities-group-entry-configurations activities-group-entry-configurations-${taskEntriesLayout}`}
                  >
                    <Box
                      className="top"
                      sx={{
                        gridTemplateColumns: (() => {
                          let result = '';
                          if (
                            activitiesGroup.carePlanConfigurationLayout &&
                            activitiesGroup.carePlanConfigurationLayout.desktopColumnCount
                          ) {
                            for (
                              let i = 0;
                              i < activitiesGroup.carePlanConfigurationLayout.desktopColumnCount;
                              i++
                            ) {
                              result += `${
                                296 / activitiesGroup.carePlanConfigurationLayout.desktopColumnCount
                              }px `;
                            }
                          } else {
                            result = '296px';
                          }
                          return result;
                        })(),
                      }}
                    >
                      {configurationsIndex &&
                        configurationsValuesIndex &&
                        activitiesGroup.carePlanConfigurationLayout &&
                        activitiesGroup.carePlanConfigurationLayout.configurations &&
                        activitiesGroup.carePlanConfigurationLayout.configurations.map(
                          (
                            configuration: {
                              carePlanConfigurationId: string;
                              displayOrder?: number;
                              columnsSpan?: number;
                            },
                            index: number
                          ) => {
                            const field =
                              configurationsIndex[configuration.carePlanConfigurationId];

                            const value =
                              configurationsValuesIndex[configuration.carePlanConfigurationId]
                                ?.carePlanConfigurationValue;

                            if (field?.type === 'empty-space')
                              return (
                                <Box
                                  key={`activities-group-entry-configuration-${templateId}-${configuration.carePlanConfigurationId}-${index}`}
                                  sx={{
                                    gridColumn: `span ${configuration.columnsSpan ?? 1}`,
                                  }}
                                />
                              );

                            if (value === undefined)
                              return (
                                <Box
                                  key={`activities-group-entry-configuration-${templateId}-${configuration.carePlanConfigurationId}-${index}`}
                                ></Box>
                              );

                            return (
                              <Box
                                key={`activities-group-entry-configuration-${templateId}-${configuration.carePlanConfigurationId}-${index}`}
                                className="item"
                                sx={{
                                  gridColumn: `span ${configuration.columnsSpan ?? 1}`,
                                }}
                              >
                                <CarePlanSheetActivitiesGroupEntryConfigurationValue
                                  configuration={field}
                                  value={value}
                                  onEditMode={editMode}
                                />
                              </Box>
                            );
                          }
                        )}
                    </Box>
                    {notesConfigurationInfo && (
                      <div className="bottom">
                        <CarePlanSheetActivitiesGroupEntryConfigurationValue
                          configuration={notesConfigurationInfo.configuration}
                          value={notesConfigurationInfo.value}
                          onEditMode={editMode}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          {(activityTypes === undefined || activityTypes.length === 0) && !editMode ? (
            <NoActivities layout={taskEntriesLayout} />
          ) : (
            <div className={`col-card col-activities col-activities-${taskEntriesLayout}`}>
              <div className="col-title">
                <Typography variant="h5">
                  {t(translations.activities_group_entry.activity_types)}
                </Typography>
              </div>
              <div className="col-edit">
                <Tooltip.NotAllowedIf
                  condition={!groupRepetitionConfigurationType}
                  title={t(translations.activities_group_entry.configure_first)}
                >
                  <Zoom in={editMode} mountOnEnter unmountOnExit>
                    <Stack flexDirection="row" gap="4px">
                      <Stack
                        flexDirection="row"
                        gap="4px"
                        sx={{
                          color:
                            hasSubActivities && groupRepetitionConfigurationType
                              ? Pallete.blue.base
                              : Pallete.gray.light,
                          pointerEvents:
                            hasSubActivities && groupRepetitionConfigurationType ? 'all' : 'none',
                        }}
                      >
                        <Typography
                          variant="h5"
                          aria-controls={
                            activitiesTypesListMenuAnchor ? 'activites-menu-add' : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={
                            Boolean(activitiesTypesListMenuAnchor) ? 'true' : undefined
                          }
                          onClick={(event) => {
                            // if only one option - follow immediatly to create/add modal
                            if (activitiesTypesWithinGroup?.length === 1) {
                              onOpenCreateActivityGroupEntry(activitiesTypesWithinGroup[0]);
                              setActivitiesTypesListMenuAnchor(null);
                            } else {
                              // else display activities types within group list
                              setActivitiesTypesListMenuAnchor(event.currentTarget);
                            }
                          }}
                          sx={{
                            color: 'currentColor',
                          }}
                        >
                          {t(translations.activities_group_entry.add_activities)}
                        </Typography>
                        <ArrowDropDown fontSize="small" sx={{color: 'currentColor'}} />
                      </Stack>
                      {activitiesTypesWithinGroup && activitiesTypesWithinGroup?.length > 1 && (
                        <Menu
                          id="activites-menu-add"
                          anchorEl={activitiesTypesListMenuAnchor}
                          open={Boolean(activitiesTypesListMenuAnchor)}
                          onClose={() => setActivitiesTypesListMenuAnchor(null)}
                          sx={{
                            transformOrigin: 'bottom 20px',
                            '.MuiList-root': {
                              padding: '4px 0',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px',
                            },
                          }}
                          transformOrigin={{horizontal: 'left', vertical: 'top'}}
                          anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
                        >
                          {activitiesTypesWithinGroup.map(
                            (activityType: ActivityType, index: number) => {
                              const isDisabled =
                                !authState.session?.selectedAffiliation?.contractsOverview
                                  ?.includesDigitalInclusion &&
                                activityType.dataType === 'application-usage';
                              return (
                                <Tooltip.NotAllowedIf
                                  key={`activities-group-card-${activityType.id}_${index}_tooltip`}
                                  condition={isDisabled}
                                  title={
                                    <Trans
                                      values={{
                                        module: t(translations.office.access['digital_inclusion']),
                                      }}
                                    >
                                      {t(translations.office.access.tooltip)}
                                    </Trans>
                                  }
                                >
                                  <MenuItem
                                    key={`header_${activityType.name}_option`}
                                    onClick={() => {
                                      if (isDisabled) return;
                                      onOpenCreateActivityGroupEntry(activityType);
                                    }}
                                    sx={{
                                      height: '28px',
                                      padding: '8px 12px',
                                      flexDirection: 'row',
                                      gap: '8px',
                                      alignItems: 'center',
                                      color: 'var(--dcm-gray)',
                                      textDecoration: isDisabled ? 'line-through' : undefined,
                                      '&:hover': {
                                        backgroundColor: Pallete.blue.bgseniormenu,
                                        textDecoration: isDisabled ? 'line-through' : undefined,
                                      },
                                    }}
                                  >
                                    <Typography
                                      component="label"
                                      sx={{
                                        color: 'currentColor',
                                        fontFamily: 'Roboto',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                      }}
                                    >
                                      {activityType.name}
                                    </Typography>
                                  </MenuItem>
                                </Tooltip.NotAllowedIf>
                              );
                            }
                          )}
                        </Menu>
                      )}
                    </Stack>
                  </Zoom>
                </Tooltip.NotAllowedIf>
              </div>

              <div className="col-content">
                {activityTypes &&
                  activityTypes.map((activityType) => {
                    return (
                      <CarePlanSheetActivityTypeEntry
                        key={`activities-group-entry-${templateId}-activity-type-${activityType.id}`}
                        activitiesSectorId={activitiesSectorId}
                        activitiesGroupId={activitiesGroupId}
                        entry={activityType}
                        layoutType={taskEntriesLayout}
                        onEditMode={editMode}
                        periodsOfTheDay={periodsOfTheDay}
                        onClick={() => {
                          if (editMode && canWrite) {
                            onOpenEditActivityGroupEntry(activityType);
                          } else {
                            onOpenViewActivityGroupEntry(activityType);
                          }
                        }}
                      />
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}
    </Box>
  );
};
